"use client";
import { useState, useCallback, useEffect } from "react";

export interface KPIItem {
  id: string;
  label: string;
  value: number;
  formattedValue: string;
  change: number;
  changeLabel: string;
  trend: "up" | "down" | "stable";
}

export interface ChartSeriesData {
  categories: string[];
  series: { name: string; data: number[]; color?: string }[];
}

export interface PieData {
  items: { name: string; value: number }[];
}

export interface ActivityItem {
  id: string;
  type: "approve" | "reject" | "create" | "update" | "comment" | "system";
  message: string;
  user: string;
  timestamp: Date;
}

const mockKPIs: KPIItem[] = [
  { id: "revenue", label: "Total Revenue", value: 2847500, formattedValue: "$2.85M", change: 12.5, changeLabel: "vs last month", trend: "up" },
  { id: "expenses", label: "Total Expenses", value: 1923000, formattedValue: "$1.92M", change: -3.2, changeLabel: "vs last month", trend: "down" },
  { id: "invoices", label: "Open Invoices", value: 342, formattedValue: "342", change: 8.1, changeLabel: "vs last month", trend: "up" },
  { id: "close-rate", label: "Close Completion", value: 87, formattedValue: "87%", change: 5.0, changeLabel: "vs last month", trend: "up" },
];

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const mockRevenueExpense: ChartSeriesData = {
  categories: months,
  series: [
    { name: "Revenue", data: [420, 380, 510, 490, 560, 580, 620, 590, 650, 710, 690, 780] },
    { name: "Expenses", data: [310, 290, 360, 340, 380, 390, 410, 400, 430, 460, 440, 490] },
  ],
};

const mockExpenseBreakdown: PieData = {
  items: [
    { name: "Payroll", value: 45 },
    { name: "Operations", value: 20 },
    { name: "Marketing", value: 15 },
    { name: "R&D", value: 12 },
    { name: "Other", value: 8 },
  ],
};

const mockCashFlow: ChartSeriesData = {
  categories: months,
  series: [
    { name: "Cash Flow", data: [110, 90, 150, 150, 180, 190, 210, 190, 220, 250, 250, 290] },
  ],
};

const mockActivities: ActivityItem[] = [
  {
    id: "1",
    type: "approve",
    message: "Invoice #INV-2024-0892 approved",
    user: "Sarah Chen",
    timestamp: new Date(Date.now() - 1800000),
  },
  {
    id: "2",
    type: "create",
    message: "Purchase order PO-4521 created",
    user: "Mike Johnson",
    timestamp: new Date(Date.now() - 3600000),
  },
  {
    id: "3",
    type: "update",
    message: "Vendor payment batch #B-0089 processed",
    user: "System",
    timestamp: new Date(Date.now() - 7200000),
  },
  {
    id: "4",
    type: "comment",
    message: "Note added to expense report EXP-1205",
    user: "Lisa Park",
    timestamp: new Date(Date.now() - 10800000),
  },
  {
    id: "5",
    type: "reject",
    message: "Credit memo CM-0332 rejected",
    user: "David Kim",
    timestamp: new Date(Date.now() - 14400000),
  },
];

export interface DashboardController {
  kpis: KPIItem[];
  revenueExpenseChart: ChartSeriesData;
  expenseBreakdown: PieData;
  cashFlowChart: ChartSeriesData;
  activities: ActivityItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDashboardController(): DashboardController {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(t);
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 600);
  }, []);

  return {
    kpis: mockKPIs,
    revenueExpenseChart: mockRevenueExpense,
    expenseBreakdown: mockExpenseBreakdown,
    cashFlowChart: mockCashFlow,
    activities: mockActivities,
    loading,
    error: null,
    refresh,
  };
}
